import { invalidate, useQueryClient } from "@msuite/ui/query";
import { InvoiceStateTag } from "@msuite/ui/tag";
import type { Enums, ProjectInvoice, ProjectInvoiceWithProject } from "@msuite/utils/types";
import { useCitadel } from "@/hooks/use-citadel";
import { client } from "@/hono";
import { InvoicesStateSelect } from "./invoices-state-select";

/** Props Interface */
interface InvoicesTableStateProps {
	invoice: ProjectInvoiceWithProject | ProjectInvoice;
}

export function InvoicesTableState({ invoice }: InvoicesTableStateProps) {
	/** Hooks */
	const qc = useQueryClient();
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnChange(value: Enums<"outgoingInvoiceState"> | "all") {
		try {
			if (value === "all") return;
			await client.projects[":projectId"].invoice[":invoiceId"].$patch({
				json: {
					...invoice,
					state: value,
					uploadResults: [],
				},
				param: {
					invoiceId: invoice.id,
					projectId: invoice.projectId,
				},
			});
			await qc.invalidateQueries({ predicate: invalidate(["invoices"]) });
		} catch (error) {
			console.error(error);
		}
	}

	/** Computed */
	// The cell looks like a filter and is a write, so it is the one invoice surface that has to be
	// gated where it is rendered. Denied, it still says what the state is — it just stops offering
	// to change it. Rules still loading keep the control, the same three-state guard `Can`
	// documents.
	const updateIsDenied = !!citadel && citadel.cannot("update", "Invoice", { ...invoice });

	/** Guard */
	if (updateIsDenied) {
		return (
			<div
				className="px-2 w-full"
				key={invoice.id}
			>
				<InvoiceStateTag state={invoice.state} />
			</div>
		);
	}

	/** Render */
	return (
		<div
			className="px-2 w-full"
			key={invoice.id}
		>
			<InvoicesStateSelect
				className="py-1 rounded-sm"
				defaultValue={invoice.state}
				onChange={handleOnChange}
			/>
		</div>
	);
}
